import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import { listSimulations, createSimulation } from '../../../utils/api';
import { Simulation } from './SimulationCard';
import IconWrapper from '../../IconWrapper';
import { FiExternalLink, FiPlay, FiClock } from 'react-icons/fi';
import { toast } from 'react-toastify';
import { useNavigation } from '../../../contexts/NavigationContext';

interface SimulationsPanelProps {
  signalId: string;
  onCreateSimulation?: (simulationId: string) => void;
}

const SimulationsPanel: React.FC<SimulationsPanelProps> = ({
  signalId,
  onCreateSimulation,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { navigateWithReferrer } = useNavigation();
  const [simulations, setSimulations] = useState<Simulation[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isCreating, setIsCreating] = useState(false);

  useEffect(() => {
    const fetchSimulations = async () => {
      try {
        setIsLoading(true);
        const data = await listSimulations(signalId);
        const simulationsArray = Array.isArray(data) ? data : (data?.simulations || data?.data || []);
        setSimulations(simulationsArray);
      } catch (error) {
        console.error('Failed to fetch simulations:', error);
        toast.error('Failed to load cases');
      } finally {
        setIsLoading(false);
      }
    };

    if (signalId) {
      fetchSimulations();
    }
  }, [signalId]);

  const handleViewSimulation = (simulationId: string) => {
    navigateWithReferrer(`/v3/ombudsman/simulations/${simulationId}`, location.pathname);
  };

  const handleStartSimulation = async () => {
    try {
      setIsCreating(true);
      const response = await createSimulation(signalId);
      if (response.id) {
        toast.success('Case guidance started');
        if (onCreateSimulation) {
          onCreateSimulation(response.id);
        }
        navigateWithReferrer(`/v3/ombudsman/simulations/${response.id}`, location.pathname);
      } else {
        throw new Error('Case guidance creation failed');
      }
    } catch (error) {
      console.error('Failed to create case guidance:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to start case guidance');
    } finally {
      setIsCreating(false);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getStatusBadgeVariant = (status: string) => {
    switch (status.toLowerCase()) {
      case 'completed':
        return 'default';
      case 'processing':
        return 'secondary';
      case 'failed':
        return 'destructive';
      default:
        return 'outline';
    }
  };

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Case Guidance</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-sm text-muted-foreground">Loading cases...</div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="text-base">Case Guidance</CardTitle>
          <Button
            size="sm"
            onClick={handleStartSimulation}
            disabled={isCreating}
            className="flex items-center gap-2"
          >
            <IconWrapper icon={FiPlay} className="h-4 w-4" />
            {isCreating ? 'Starting...' : 'New Case'}
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {simulations.length === 0 ? (
          <div className="text-center py-6">
            <p className="text-sm text-muted-foreground mb-4">
              No cases yet for this signal.
            </p>
            <Button
              size="sm"
              onClick={handleStartSimulation}
              disabled={isCreating}
              variant="outline"
            >
              <IconWrapper icon={FiPlay} className="h-4 w-4 mr-2" />
              Start First Case
            </Button>
          </div>
        ) : (
          <div className="space-y-3">
            {simulations.map((simulation) => (
              <div
                key={simulation.id}
                className="flex items-center justify-between p-3 border rounded-lg hover:bg-gray-50 transition-colors cursor-pointer"
                onClick={() => handleViewSimulation(simulation.id)}
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-sm font-medium truncate">
                      {simulation.signal_summary || simulation.name || `Simulation ${simulation.id.slice(0, 8)}`}
                    </span>
                    <Badge variant={getStatusBadgeVariant(simulation.status)} className="text-xs">
                      {simulation.status}
                    </Badge>
                  </div>
                  <div className="flex items-center gap-2 text-xs text-muted-foreground">
                    <IconWrapper icon={FiClock} className="h-3 w-3" />
                    <span>{formatDate(simulation.created_at)}</span>
                    {simulation.predicted_outcome && (
                      <>
                        <span>•</span>
                        <span className="capitalize">
                          {simulation.predicted_outcome.replace('_', ' ')}
                        </span>
                        {simulation.confidence_score !== undefined && (
                          <span>
                            ({Math.round(simulation.confidence_score > 1 ? simulation.confidence_score : simulation.confidence_score * 100)}%)
                          </span>
                        )}
                      </>
                    )}
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleViewSimulation(simulation.id);
                  }}
                  className="flex-shrink-0 ml-2"
                >
                  <IconWrapper icon={FiExternalLink} className="h-4 w-4" />
                </Button>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default SimulationsPanel;

